import { useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api'
import './CustomerSetup.css'

type CustomerSetupItem = {
  id: string
  full_name: string
  email?: string
  phone?: string
  company_name?: string
  company_purpose?: string
  license_key?: string
  expires_at?: string
  days_remaining?: number
}

const PURPOSE_LABELS: Record<string, string> = {
  deliveries: 'משלוחים',
  agriculture: 'חקלאות',
  security: 'אבטחה',
  mapping: 'מיפוי',
  inspection: 'בדיקות',
  photography: 'צילום',
  other: 'אחר',
}

function CountdownBadge({ days }: { days?: number }) {
  if (days == null) return <span className="countdown-none">—</span>
  if (days === 0) return <span className="countdown-expired">פג תוקף</span>
  const cls = days <= 30 ? 'countdown-warn' : days <= 90 ? 'countdown-ok' : 'countdown-good'
  return <span className={`countdown-badge ${cls}`}>{days} ימים</span>
}

export default function CustomerSetup() {
  const navigate = useNavigate()

  const { data: records = [], isLoading, error } = useQuery<CustomerSetupItem[]>({
    queryKey: ['customer-setup'],
    queryFn: () => api.get('/customer-setup').then(r => r.data),
  })

  return (
    <div className="cs-page">
      <div className="cs-header">
        <div className="cs-header-left">
          <Link to="/settings" className="cs-back">← הגדרות</Link>
          <h1>הגדרת לקוחות</h1>
        </div>
        <button className="btn-primary" onClick={() => navigate('/settings/customer-setup/new')}>
          + לקוח חדש
        </button>
      </div>

      {isLoading && <p className="cs-loading">טוען...</p>}
      {error && <p className="cs-error">שגיאה בטעינת נתונים</p>}

      {!isLoading && !error && (
        <div className="cs-table-wrap">
          {records.length === 0 ? (
            <p className="cs-empty">אין לקוחות. צור לקוח חדש באמצעות הכפתור למעלה.</p>
          ) : (
            <table className="cs-table">
              <thead>
                <tr>
                  <th>שם לקוח</th>
                  <th>חברה</th>
                  <th>תחום פעילות</th>
                  <th>מספר רישיון</th>
                  <th>תפוגת רישיון</th>
                  <th>זמן שנותר</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id}>
                    <td>
                      <div>{r.full_name}</div>
                      {r.email && <small className="cs-sub" dir="ltr">{r.email}</small>}
                    </td>
                    <td>{r.company_name || '—'}</td>
                    <td>
                      {r.company_purpose
                        ? <span className="purpose-badge">{PURPOSE_LABELS[r.company_purpose] ?? r.company_purpose}</span>
                        : '—'}
                    </td>
                    <td dir="ltr">{r.license_key || '—'}</td>
                    <td>{r.expires_at ? new Date(r.expires_at).toLocaleDateString('he-IL') : '—'}</td>
                    <td><CountdownBadge days={r.days_remaining} /></td>
                    <td>
                      <button
                        className="cs-action-btn"
                        onClick={() => navigate(`/settings/customer-setup/${r.id}`)}
                      >
                        עריכה / צפייה
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}
